import { LogicalAndFilter } from '@cubejs-client/core';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

import { HCP_HCO_CUBE_NAME } from '@/api/hco/constants';
import { buildResolverScopeFilters } from '@/api/hco/filters';
import { HCOKeys } from '@/api/queries/hco/cacheKeys';
import useQuery from '@/api/queries/useQuery';
import { FilterLimitPresets } from '@/models/filter/filterDefaultLimits';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { toNumberOrNull, toStringOrNull } from '@/utils/cube/mapper';
import { useAllowlistedGlobalFilters } from '@/utils/cube/useGetGlobalFilters';

const RESOLVER_LIMIT = FilterLimitPresets.LIMIT_50K;

const HCP_BK_DIMENSION = `${HCP_HCO_CUBE_NAME}.hcp_bk`;

const EMPTY_HCP_BKS: string[] = [];

interface ResolverResponse {
  totalCount?: number;
  data?: CubeRawRow[];
}

interface ResolverResult {
  hcpBks: string[];
  totalCount: number;
}

const EMPTY_RESOLVER_RESULT: ResolverResult = { hcpBks: EMPTY_HCP_BKS, totalCount: 0 };

export interface HcoFilteredHcpBks {
  hcpBks: string[];
  isResolving: boolean;
  isError: boolean;
  isActive: boolean;
  isTruncated: boolean;
}

const countActivePredicates = (filters: LogicalAndFilter): number => filters.and.length;

const selectResolverResult = ({ totalCount, data }: ResolverResponse): ResolverResult => {
  if (!data) return EMPTY_RESOLVER_RESULT;
  const seen = new Set<string>();
  data.forEach(hcpRow => {
    const hcpBk = toStringOrNull(hcpRow[HCP_BK_DIMENSION]);
    if (hcpBk) seen.add(hcpBk);
  });
  return { hcpBks: Array.from(seen), totalCount: toNumberOrNull(totalCount) ?? seen.size };
};

// Rooted on hcp_hco (not hcp_in_hco) because it is the only HCO cube that joins to every filterable HCP/HCO attribute cube; rooting on hcp_in_hco is why Territory/Area-of-Interest/Other-Specialty/Segmentation filters returned zero rows.
export const useHcoFilteredHcpBks = (hcoBk: string | undefined, includeAllDepartments: boolean): HcoFilteredHcpBks => {
  const globalFilters = useAllowlistedGlobalFilters({
    includeHco: includeAllDepartments,
    excludeDisabled: false,
  });

  const hasActiveFilters = countActivePredicates(globalFilters) > 0;
  const isActive = hasActiveFilters && !!hcoBk;

  const filters = useMemo(
    () => [globalFilters, ...buildResolverScopeFilters(hcoBk ?? '', includeAllDepartments)],
    [globalFilters, hcoBk, includeAllDepartments],
  );

  const { data, isLoading, isError } = useQuery<ResolverResponse, AxiosError, ResolverResult>({
    payload: {
      dimensions: [HCP_BK_DIMENSION],
      filters,
      limit: RESOLVER_LIMIT,
      total: true,
    },
    options: {
      queryKey: HCOKeys.filteredHcpBks(hcoBk, includeAllDepartments, globalFilters),
      select: selectResolverResult,
      enabled: isActive,
    },
  });

  const isTruncated = isActive && (data?.totalCount ?? 0) > RESOLVER_LIMIT;

  return {
    hcpBks: data?.hcpBks ?? EMPTY_HCP_BKS,
    isResolving: isActive && isLoading,
    isError,
    isActive,
    isTruncated,
  };
};
